import React, { useMemo } from 'react';
import styled from 'astroturf/react';
import { Body } from 'js/design_system/Body';
import { Heading } from 'js/design_system/Heading';
import { UsherTasks } from 'js/utils/usher/tasks';
import {
  EvaluationPublicationCard,
  Publication,
} from './EvaluationPublicationCard';

type EvaluationResult =
  UsherTasks['citeright_evaluate_publication_finder']['result'];
export type ClaimResult = EvaluationResult[number];

/**
 * Deduplicate publications by work_id, keeping the first occurrence.
 * The LLM sometimes repeats publications when there aren't enough unique candidates.
 * This is more likely to happen in dev where ES isn't returning results
 */
function deduplicatePublications(publications: Publication[]): Publication[] {
  const seen = new Set<number>();
  return publications.filter((pub) => {
    if (seen.has(pub.work_id)) {
      return false;
    }
    seen.add(pub.work_id);
    return true;
  });
}

const ClaimSection = styled.div`
  background: var(--background-white);
  border-radius: var(--border-radius-radius-md);
  padding: var(--spacing-sm-24);
  margin-bottom: var(--spacing-xs-16);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const ClaimText = styled.div`
  font-style: italic;
  background: var(--brand-colors-neutral-200);
  padding: var(--spacing-xs-12);
  border-left: 3px solid var(--brand-colors-blue-500);
  margin: var(--spacing-xs-12) 0;
`;

const MetaRow = styled.div`
  display: flex;
  gap: var(--spacing-xs-16);
  flex-wrap: wrap;
  margin-top: var(--spacing-xs-8);
  font-size: 13px;
  color: var(--text-secondary);
`;

interface EvaluationClaimCardProps {
  claimResult: ClaimResult;
  index: number;
}

export const EvaluationClaimCard: React.FC<EvaluationClaimCardProps> = ({
  claimResult,
  index,
}) => {
  // Deduplicate publications - the LLM sometimes repeats when candidates are limited
  const uniquePublications = useMemo(
    () => deduplicatePublications(claimResult.ranked_publications || []),
    [claimResult.ranked_publications],
  );

  return (
    <ClaimSection>
      <Heading as="h4" size="sm">
        Claim {index + 1}{' '}
        {claimResult.claim_id && `(ID: ${claimResult.claim_id})`}
      </Heading>
      <ClaimText>{claimResult.claim_text}</ClaimText>

      {uniquePublications.length > 0 ? (
        <>
          <Body
            size="sm"
            style={{
              fontWeight: 600,
              marginTop: '16px',
            }}
          >
            Ranked Publications ({uniquePublications.length})
          </Body>
          {uniquePublications.map((pub) => (
            <EvaluationPublicationCard key={pub.work_id} publication={pub} />
          ))}
        </>
      ) : (
        <Body size="sm" color="secondary" style={{ marginTop: '16px' }}>
          No ranked publications found.
        </Body>
      )}

      <MetaRow style={{ marginTop: '16px' }}>
        <span>
          <strong>Vector Search Results:</strong>{' '}
          {claimResult.vector_search_results?.length ?? 'N/A'}
        </span>
        <span>
          <strong>Web Search Results:</strong>{' '}
          {claimResult.web_search_results?.length ?? 'N/A'}
        </span>
      </MetaRow>
    </ClaimSection>
  );
};

export default EvaluationClaimCard;
